import {
  CAMPOS_DE_TOTALES_REPORTADOS,
  type CampoEditable,
  type EdicionDeExportacion,
  type ExportacionDetalle,
} from '@cnc/contracts';
import {useMemo, useState} from 'react';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useCatalogosDeSeguimiento, useEditarExportacion, useExportacion} from '../api/seguimiento';
import {
  aTexto,
  CAMPOS_EDITABLES,
  convertirValor,
  GRUPOS_DE_CAMPOS,
  valorDeFila,
} from '../componentes/campos-exportacion';
import {EntradaDeCampo} from '../componentes/entrada-de-campo';
import {claveDeTexto, tr, trx} from '../i18n/idioma';
import {direccionDeVuelta} from '../utilidades/vuelta';

// Valor de cada campo como texto en el formulario ('' = vacio).
type Formulario = Record<CampoEditable, string>;

function formularioDesde(x: ExportacionDetalle): Formulario {
  return Object.fromEntries(CAMPOS_EDITABLES.map(c => [c.clave, aTexto(valorDeFila(x, c.clave))])) as Formulario;
}

// Formulario de edicion de una exportacion. Solo se envian los campos que
// cambiaron; el servidor deja cada uno en la bitacora.
export function PaginaEditarExportacion() {
  const {codigo = ''} = useParams();
  const navegar = useNavigate();
  // Desde Calidad de datos ("✎ Corregir"): al guardar se vuelve a la revision.
  const [parametros] = useSearchParams();
  const volver = direccionDeVuelta(parametros);
  const consulta = useExportacion(codigo);
  const catalogos = useCatalogosDeSeguimiento();
  const editar = useEditarExportacion(codigo);
  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const inicial = useMemo(() => (consulta.data ? formularioDesde(consulta.data) : null), [consulta.data]);
  const valores = formulario ?? inicial;
  // Con Big Bags en el inventario los totales salen de las bolsas: los
  // reportados no se editan (Daniel, 16/10/2026).
  const conBolsas = (consulta.data?.listaDeBigBags.length ?? 0) > 0;
  const bloqueado = (clave: CampoEditable) =>
    conBolsas && (CAMPOS_DE_TOTALES_REPORTADOS as readonly CampoEditable[]).includes(clave);

  const cambiar = (clave: CampoEditable, valor: string) =>
    setFormulario(actual => ({...(actual ?? inicial!), [clave]: valor}));

  const guardar = async () => {
    if (!valores || !inicial || !consulta.data) return;
    setMensaje(null);
    const cambios: Record<string, unknown> = {version: consulta.data.version};
    for (const clave of Object.keys(valores) as CampoEditable[]) {
      if (valores[clave] === inicial[clave]) continue;
      const convertido = convertirValor(clave, valores[clave]);
      if ('error' in convertido) {
        setMensaje(convertido.error);
        return;
      }
      cambios[clave] = convertido.valor;
    }
    if (Object.keys(cambios).length === 1) {
      setMensaje(tr('No hay cambios que guardar.', 'There are no changes to save.'));
      return;
    }
    try {
      const detalle = await editar.mutateAsync(cambios as EdicionDeExportacion);
      void navegar(volver ?? `/seguimiento/${encodeURIComponent(detalle.codigo)}`);
    } catch (error) {
      setMensaje(error instanceof ErrorDeApi ? error.message : tr('No se pudo guardar.', 'Could not save.'));
      if (error instanceof ErrorDeApi && error.detalles?.length)
        setMensaje(`${error.message} ${error.detalles.join(' ')}`);
    }
  };

  return (
    <section className="max-w-5xl">
      <Link to={volver ?? `/seguimiento/${encodeURIComponent(codigo)}`} className="text-sm text-verde hover:underline">
        {volver
          ? tr('← Volver a la revisión de calidad', '← Back to the quality check')
          : tr('← Volver a la exportación', '← Back to the export')}
      </Link>
      <h1 className="mt-3 text-2xl font-semibold text-verde-oscuro">
        {tr('Editar exportación', 'Edit export')} {codigo}
      </h1>
      <p className="text-sm text-apagado">
        {tr(
          'Cambia lo que necesites y guarda. Cada cambio queda en el historial de la exportación con el valor anterior y el nuevo.',
          'Change what you need and save. Every change is kept in the export history with the previous and the new value.',
        )}
      </p>
      {consulta.isPending && <p className="mt-4 text-apagado">{tr('Consultando…', 'Loading…')}</p>}
      {consulta.isError && <p className="mt-4 text-sm text-alerta">{consulta.error.message}</p>}
      {valores && consulta.data && (
        <form
          className="mt-4 space-y-4"
          onSubmit={e => {
            e.preventDefault();
            void guardar();
          }}
        >
          {GRUPOS_DE_CAMPOS.map(grupo => (
            <fieldset key={claveDeTexto(grupo.titulo)} className="rounded-lg border border-linea bg-white p-5">
              <legend className="px-1 font-medium">{trx(grupo.titulo)}</legend>
              {grupo.nota && <p className="mb-3 text-xs text-apagado">{trx(grupo.nota)}</p>}
              {grupo.campos.some(c => bloqueado(c.clave)) && (
                <p className="mb-3 text-xs text-apagado" data-prueba="totales-bloqueados">
                  🔒{' '}
                  {tr(
                    'La exportación tiene Big Bags en el inventario: sus totales salen de ellos y los reportados no se editan.',
                    'The export has Big Bags in the inventory: its totals come from them and the reported ones are not edited.',
                  )}
                </p>
              )}
              <div className={`grid gap-3 ${grupo.campos.length > 1 ? 'md:grid-cols-2 xl:grid-cols-3' : ''}`}>
                {grupo.campos.map(campo => (
                  <fieldset key={campo.clave} disabled={bloqueado(campo.clave)} className="contents">
                    <EntradaDeCampo
                      campo={campo}
                      valor={valores[campo.clave]}
                      cambiado={valores[campo.clave] !== inicial?.[campo.clave]}
                      catalogos={catalogos.data}
                      onCambiar={v => cambiar(campo.clave, v)}
                    />
                  </fieldset>
                ))}
              </div>
            </fieldset>
          ))}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={editar.isPending}
              className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {editar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar cambios', 'Save changes')}
            </button>
            <button
              type="button"
              onClick={() => setFormulario(null)}
              className="rounded-md border border-linea bg-white px-4 py-2 text-sm hover:bg-papel"
            >
              {tr('Deshacer', 'Undo')}
            </button>
            {mensaje && <span className="text-sm text-alerta">{mensaje}</span>}
          </div>
        </form>
      )}
    </section>
  );
}
